import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// 支援語系（zh-TW 為 source of truth，其餘可缺 key → fallback 回 zh-TW）
export const SUPPORTED_LANGS = [
    { code: 'zh-TW', label: '繁體中文', flag: '🇹🇼' },
    { code: 'zh-CN', label: '简体中文', flag: '🇨🇳' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'ja', label: '日本語', flag: '🇯🇵' },
    { code: 'ko', label: '한국어', flag: '🇰🇷' },
    { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
    { code: 'th', label: 'ไทย', flag: '🇹🇭' },
    { code: 'ms', label: 'Bahasa Melayu', flag: '🇲🇾' },
];
export const SUPPORTED_CODES = SUPPORTED_LANGS.map((l) => l.code);
export const LANG_STORAGE_KEY = 'qubby-admin:lang';

// 把任意 browser locale / 偵測值正規化成我們支援的 8 碼
export function normalizeLang(lng) {
    if (!lng) return 'zh-TW';
    const low = String(lng).toLowerCase();
    // 簡體：zh-cn / zh-hans / zh-sg → zh-CN（要先於 zh 一般判斷）
    if (low.startsWith('zh-cn') || low.startsWith('zh-hans') || low.startsWith('zh-sg')) return 'zh-CN';
    if (low.startsWith('zh')) return 'zh-TW'; // 其餘中文（tw/hant/hk/mo）→ 繁中
    if (low.startsWith('ja')) return 'ja';
    if (low.startsWith('th')) return 'th';
    if (low.startsWith('ko')) return 'ko';
    if (low.startsWith('vi')) return 'vi';
    if (low.startsWith('ms')) return 'ms';
    if (low.startsWith('en')) return 'en';
    return 'en'; // 未支援的 browser 語系 → 英文（國際預設）
}

// 對應到 Intl 用的完整 locale（日期/數字格式化）
const INTL_LOCALE = {
    'zh-TW': 'zh-TW',
    'zh-CN': 'zh-CN',
    en: 'en-US',
    ja: 'ja-JP',
    th: 'th-TH',
    ko: 'ko-KR',
    vi: 'vi-VN',
    ms: 'ms-MY',
};
export function intlLocale(lng) {
    return INTL_LOCALE[normalizeLang(lng || i18n.language)] || 'en-US';
}

// 自動載入 ./locales/{lang}/{namespace}.json
const modules = import.meta.glob('./locales/**/*.json', { eager: true });
const resources = {};
const nsSet = new Set();
for (const path in modules) {
    const m = path.match(/\.\/locales\/([^/]+)\/(.+)\.json$/);
    if (!m) continue;
    const [, lng, ns] = m;
    resources[lng] = resources[lng] || {};
    resources[lng][ns] = modules[path].default || modules[path];
    nsSet.add(ns);
}

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        fallbackLng: 'zh-TW',
        supportedLngs: SUPPORTED_CODES,
        ns: Array.from(nsSet),
        defaultNS: 'common',
        load: 'currentOnly',
        returnEmptyString: false,
        // 字典全用 flat dotted key（如 "enum.direction.outbound"）。預設 keySeparator='.'
        // 會把它當巢狀路徑解析失敗→回 key 原文。關掉它，保留 nsSeparator ':' 供 t('common:xxx')。
        keySeparator: false,
        nsSeparator: ':',
        detection: {
            order: ['localStorage', 'navigator'],
            lookupLocalStorage: LANG_STORAGE_KEY,
            caches: ['localStorage'],
            convertDetectedLanguage: normalizeLang,
        },
        interpolation: { escapeValue: false },
    });

// <html lang> 跟著切
const applyHtmlLang = (lng) => {
    if (typeof document !== 'undefined') document.documentElement.lang = normalizeLang(lng);
};
applyHtmlLang(i18n.language);
i18n.on('languageChanged', applyHtmlLang);

export default i18n;
